<template>
  <div>
    <question-editor :value="{title: 'Mobile Number', questionType: 'TEXT'}" :is-static="true"></question-editor>
    <draggable
      class="question-container"
      draggable=".draggable"
      group="questions"
      handle=".header"
      ghost-class="question-ghost"
      v-model="questionList"
      @change="handleChange"
      :class="{ empty: isEmpty }"
    >
      <div
        v-for="(question, index) in questionList"
        :key="question.id"
        class="item"
        :class="{draggable: isEditable}"
      >
        <question-editor
          :value="questionList[index]"
          :index="index + 1"
          :is-selected="question.id === canvasSelectedQuestionId"
          :isEditable="isEditable"
          @input="handleSetQuestion(index, $event)"
          @onSelect="handleSelectQuestion(question.id)"
          @onRemove="handleRemoveQuestion(index)"
          @onEdit="handleEditQuestion(question)"
        ></question-editor>
      </div>
      <div slot="footer" v-if="isEditable">
        <div class="placeholder-container">
          Drag or double-click a qualitative question here from the qualitative questions panel on the right, or&nbsp;
          <a
            @click="handleCreateQuestion"
          >Create qualitative question</a>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import draggable from 'vuedraggable';

import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

import Empty from '@/components/common/Empty';

import QuestionEditor from './QuestionEditor';

// These question types are only available when specific survey types are set.
const DISPLAYABLE_WITH_SURVEY_TYPE = {
  MEDIA: ['MUSIC'],
};

export default {
  name: 'QuestionCanvas',
  components: {
    draggable,

    Empty,

    QuestionEditor,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState('surveybuilder', [
      'survey',
      'canvasSelectedQuestionId',
      'canvasShowChoicesModifiedMessage',
    ]),

    ...mapGetters('surveybuilder', ['canvasParsedQuestions']),

    questionList: {
      get() {
        return this.canvasParsedQuestions.filter(q => this.shouldDisplayQuestion(q));
      },
      set(questions) {
        this.$store.dispatch('surveybuilder/canvasSetQuestions', questions);
      },
    },

    isEmpty() {
      return this.questionList.length === 0;
    },
    isSelected() {
      return index => index === this.selectedQuestionIndex;
    },
    isOdd() {
      return index => index % 2 !== 0 && !this.isSelected(index);
    },
  },
  methods: {
    shouldDisplayQuestion(question) {
      // Do we have this question type as displayable?
      // if not set we default to displayable for this survey type.
      const displayableIn = DISPLAYABLE_WITH_SURVEY_TYPE[question.questionType] || [
        this.survey.surveyType,
      ];
      return displayableIn.includes(this.survey.surveyType);
    },

    handleSetQuestion(index, question) {
      const questions = Object.assign([], this.questionList, { [index]: question });
      this.questionList = questions;

      const choices = question.choices || [];
      const isAddingNewChoice = choices.some(choice => isNaN(choice.id) && choice.title === '');

      if (!isAddingNewChoice) {
        this.$store.dispatch('surveybuilder/updateCanvasQuestions');
      }
    },
    handleSelectQuestion(questionId) {
      this.$store.dispatch('surveybuilder/canvasSelectQuestion', questionId);
    },
    handleRemoveQuestion(index) {
      const questions = [...this.questionList];

      questions.splice(index, 1);
      if (this.selectedQuestionIndex >= questions.length) {
        const newIndex = questions.length - 1;
        const newId = questions[newIndex].id;
        this.handleSelectQuestion(newId);
      }
      this.questionList = questions;
      this.$store
        .dispatch('surveybuilder/updateQuestions', {
          questions: this.questionList,
          surveyId: this.survey.id,
        });
    },
    handleCreateQuestion() {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_ADD_TO_CANVAS}`,
        true,
      );
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_VISIBILITY}`,
        true,
      );
    },
    handleEditQuestion(question) {
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_FORM}`, question);
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_DIALOG_VISIBILITY}`,
        true,
      );
    },
    handleChange({ added, moved }) {
      const value = added || moved;
      if (value) {
        this.$store.dispatch('surveybuilder/canvasSelectQuestion', value.id);
        this.$store
          .dispatch('surveybuilder/updateQuestions', {
            surveyId: this.survey.id,
            questions: this.canvasParsedQuestions,
          })
          .then(() => {
            // We have the "Add new question" dashed block at the bottom which also counts as an
            // index, so chances are that if our new index is more than the amount of questions
            // we have, that the user dragged onto that section, lets adjust it here.
            let index = value.newIndex;
            if (index > this.canvasParsedQuestions.length - 1) {
              index -= 1;
            }

            this.$store.dispatch(
              'surveybuilder/canvasSelectQuestion',
              this.canvasParsedQuestions[index].id,
            );
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/style/_variables_live.scss';

.question-container {
  min-height: 400px;
  margin-bottom: 60px;
  z-index: 10;
}

.placeholder-container {
  position: absolute;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 2px 2px 2px #efefef;
  color: #ccc;
  min-height: 40px;
  margin: 0 12px;
  width: calc(100% - 42px);
  z-index: 0;
}

.empty .placeholder-container {
  margin: 0;
  width: calc(100% - 16px);
  height: calc(100vh - 275px);
}
</style>

<style>
.question-ghost {
  position: relative;
  z-index: 10;
  margin: 14px 15px;
}
.empty .question-ghost {
  margin: -4px 0;
}
</style>



// WEBPACK FOOTER //
// QuestionCanvas.vue?0572d9b7